import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { EnvironmentService } from 'src/app/core/services/environment/environment.service';
import { StoreConfigurationRepositoryService } from 'src/app/core/services/store-configuration/store-configuration-repository.service';
import { ProductSearchSuggestion } from '../../dynamic-header/category/header-data';

@Injectable({
  providedIn: 'root'
})
export class SearchSuggestionRepositoryService {
  private http = inject(HttpClient);
  private environmentService = inject(EnvironmentService);
  private storeConfigurationRepositoryService = inject(StoreConfigurationRepositoryService);

  // ===== SERVICE STATE MANAGEMENT =====
  private _serviceError = signal<string | null>(null);
  private _isLoading = signal<boolean>(false);

  // ===== PUBLIC READONLY SIGNALS =====
  readonly serviceError = this._serviceError.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();

  // ===== SEARCH SUGGESTION STATE =====
  private _url = signal<string>('');
  private _searchSuggestions = signal<ProductSearchSuggestion | null>(null);

  // Public readonly signals
  readonly url = this._url.asReadonly();
  readonly searchSuggestions = this._searchSuggestions.asReadonly();

  // Computed signals for derived state
  readonly hasSearchSuggestions = computed(() => !!this._searchSuggestions());
  readonly showOutOfStockProducts = computed(() => this.storeConfigurationRepositoryService.showOutOfStockProducts());

  // ===== SERVICE SELF-INITIALIZATION =====
  constructor() {
    // Auto-initialize when service is first injected
    this.initializeService();
  }

  // ===== SERVICE INITIALIZATION =====
  private initializeService(): void {
    this._serviceError.set(null);

    // Initialize URL synchronously
    const environment = this.environmentService.environment();
    if (environment?.url_2) {
      this._url.set(environment.url_2);
    } else {
      this._serviceError.set('Environment URL not available');
    }
  }

  // ===== UTILITY METHODS =====
  resetService(): void {
    this._serviceError.set(null);
    this._isLoading.set(false);
    this._searchSuggestions.set(null);
  }

  getServiceStatus(): {
    hasErrors: boolean;
    isLoading: boolean;
    error: string | null;
  } {
    return {
      hasErrors: !!this._serviceError(),
      isLoading: this._isLoading(),
      error: this._serviceError()
    };
  }

  // ===== SEARCH SUGGESTION OPERATIONS =====
  async getSearchSuggestion(searchValue: string): Promise<void> {
    try {
      this._isLoading.set(true);
      this._serviceError.set(null);

      const requiredParams: any = {
        search: searchValue,
        ...(this.showOutOfStockProducts() === false ? { in_stock: true } : {})
      };

      const params = new HttpParams({ fromObject: requiredParams });

      const suggestions = await firstValueFrom(
        this.http.get<ProductSearchSuggestion>(`${this.url()}/products_search_suggestions`, { params })
          .pipe(
            catchError((error) => {
              const errorMessage = error instanceof Error ? error.message : 'Failed to get search suggestions';
              this._serviceError.set(errorMessage);
              return of({} as ProductSearchSuggestion);
            })
          )
      );

      this._searchSuggestions.set(suggestions);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to get search suggestions';
      this._serviceError.set(errorMessage);
      this._searchSuggestions.set(null);
    } finally {
      this._isLoading.set(false);
    }
  }

  // Public setter method for signals
  setSearchSuggestions(suggestions: ProductSearchSuggestion | null): void {
    this._searchSuggestions.set(suggestions);
  }

  // Clear method
  clearSearchSuggestions(): void {
    this._searchSuggestions.set(null);
  }

  // Set URL method
  setUrl(url: string): void {
    this._url.set(url);
  }

  // Set loading state method
  setLoading(loading: boolean): void {
    this._isLoading.set(loading);
  }
}
